import * as seriesTypes from "@chartLib/lib/series";
import * as tooltipTypes from "@chartLib/lib/tooltip";
import * as indicatorTypes from "@chartLib/lib/indicator";
import * as coordinateTypes from "@chartLib/lib/coordinates";
import {
  StrokeType,
  ColorType,
  SourcePathType,
  WindowSizeType,
  ThresholdLineType,
  NumericPropertyType,
  BooleanType,
} from "../PropertyTypes";

const momentumRegistry = {
  displayName: "Momentum",
  indicators: {
    // BIAS — Fechamento menos MA (migrado do Silverlight FML.BIAS).
    bias: {
      displayName: "Fechamento menos MA (BIAS)",
      key: "bias",
      type: indicatorTypes.bias,
      series: seriesTypes.BIASSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "BIAS",
      renderKey: "bias",
      decimalPlaces: 4,
      defaultOptions: [
        NumericPropertyType({
          name: "l1",
          displayName: "Período 1 (L1)",
          min: 1,
          max: 1000,
          defaultValue: 6,
        }),
        NumericPropertyType({
          name: "l2",
          displayName: "Período 2 (L2)",
          min: 1,
          max: 1000,
          defaultValue: 12,
        }),
        NumericPropertyType({
          name: "l3",
          displayName: "Período 3 (L3)",
          min: 1,
          max: 1000,
          defaultValue: 24,
        }),
      ],
      defaultOptionsForAppearance: [
        // Paleta por índice do engine: [Yellow, Red, #0000B4].
        StrokeType({
          name: "strokeBias1",
          displayName: "Cor BIAS1",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeBias2",
          displayName: "Cor BIAS2",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeBias3",
          displayName: "Cor BIAS3",
          defaultValue: {
            color: "#0000B4",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "bias1", colorPath: "strokeBias1.color" },
          { key: "bias2", colorPath: "strokeBias2.color" },
          { key: "bias3", colorPath: "strokeBias3.color" },
        ],
      },
      tooltip: tooltipTypes.BIASTooltip,
      // Percentual sem limites, simétrico em torno de zero.
      yExtentKeys: ["bias"],
      yExtents: (plotData) => {
        const values = plotData
          .flatMap((d) => [d.bias?.bias1, d.bias?.bias2, d.bias?.bias3])
          .filter((v) => v !== undefined && v !== null && !isNaN(v));
        if (values.length === 0) return [-1, 1];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = Math.max((max - min) * 0.1, 1e-6);
        return [min - padding, max + padding];
      },
      description:
        "Fechamento menos MA: distância percentual entre o fechamento e três médias aritméticas de períodos diferentes. Afastamentos grandes sugerem exaustão do movimento.",
    },
    // DPO — Detrended Price Oscillator (migrado do Silverlight FML.DPO).
    // Sem parâmetros: as janelas 20/11 são fixas no engine original.
    dpo: {
      displayName: "Oscilador de Preço sem Tendência (DPO)",
      key: "dpo",
      type: indicatorTypes.dpo,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "DPO",
      renderKey: "dpo",
      decimalPlaces: 2,
      defaultOptions: [],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          // Cor default do engine (1ª série da paleta por índice).
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "DPO",
      },
      // Oscilador em unidades de preço, simétrico em torno de zero.
      yExtentKeys: ["dpo"],
      yExtents: (plotData) => {
        const values = plotData
          .map((d) => d.dpo)
          .filter((v) => v !== undefined && v !== null && !isNaN(v));
        if (values.length === 0) return [-1, 1];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = Math.max((max - min) * 0.1, 1e-6);
        return [min - padding, max + padding];
      },
      description:
        "Oscilador de Preço sem Tendência: diferença entre o fechamento e a média móvel deslocada, filtrando a tendência de fundo para evidenciar ciclos curtos e zonas de sobrecompra/sobrevenda.",
    },
    // SROC — Smoothed Price Rate-of-Change (migrado do Silverlight FML.SROC).
    // A linha de sinal (ROCMA) só é plotada quando M ≠ 0 (Visible do engine).
    sroc: {
      displayName: "ROC Suavizado (SROC)",
      key: "sroc",
      type: indicatorTypes.sroc,
      series: seriesTypes.SROCSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "SROC",
      renderKey: "sroc",
      decimalPlaces: 4,
      defaultOptions: [
        NumericPropertyType({
          name: "me",
          displayName: "Média Exp. (ME)",
          min: 1,
          max: 1000,
          defaultValue: 13,
        }),
        NumericPropertyType({
          name: "period",
          displayName: "Período (N)",
          min: 1,
          max: 1000,
          defaultValue: 9,
        }),
        NumericPropertyType({
          name: "m",
          displayName: "Média de sinal (M, 0 = oculta)",
          min: 0,
          max: 1000,
          defaultValue: 0,
        }),
      ],
      defaultOptionsForAppearance: [
        // Cores default do engine (paleta por índice): ROC=Yellow, ROCMA=Red,
        // linha zero = #0000B4 tracejada (SetAttrs "StyleDash").
        StrokeType({
          name: "strokeRoc",
          displayName: "Cor ROC",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeRocma",
          displayName: "Cor da média de sinal",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Linha zero",
          defaultValue: {
            color: "#0000B4",
            width: 1,
            opacity: 1,
            dash: "Dash",
          },
        }),
      ],
      labelConfig: {
        lines: [{ key: "roc", colorPath: "strokeRoc.color" }],
      },
      tooltip: tooltipTypes.SROCTooltip,
      // Oscilador percentual sem limites, simétrico em torno de zero.
      yExtentKeys: ["sroc"],
      yExtents: (plotData) => {
        const values = plotData
          .flatMap((d) => [d.sroc?.roc, d.sroc?.rocma])
          .filter((v) => v !== undefined && v !== null && !isNaN(v));
        if (values.length === 0) return [-1, 1];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = Math.max((max - min) * 0.1, 1e-6);
        return [min - padding, max + padding];
      },
      description:
        "ROC Suavizado: taxa de variação percentual calculada sobre uma média exponencial do fechamento, o que reduz os ruídos do ROC tradicional. A média de sinal é opcional (M = 0 a oculta).",
    },
    // DBCD — Convergência e Divergência de BIAS (migrado do Silverlight FML.DBCD).
    dbcd: {
      displayName: "Convergência e Divergência de BIAS (DBCD)",
      key: "dbcd",
      type: indicatorTypes.dbcd,
      series: seriesTypes.DBCDSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "DBCD",
      renderKey: "dbcd",
      decimalPlaces: 6,
      defaultOptions: [
        NumericPropertyType({
          name: "period",
          displayName: "Período do BIAS (N)",
          min: 1,
          max: 1000,
          defaultValue: 5,
        }),
        NumericPropertyType({
          name: "m",
          displayName: "Defasagem (M)",
          min: 1,
          max: 1000,
          defaultValue: 16,
        }),
        NumericPropertyType({
          name: "t",
          displayName: "Suavização (T)",
          min: 1,
          max: 1000,
          defaultValue: 76,
        }),
      ],
      defaultOptionsForAppearance: [
        // Cores default do engine (paleta por índice): DBCD=Yellow, MM=Red.
        StrokeType({
          name: "strokeDbcd",
          displayName: "Cor DBCD",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeMm",
          displayName: "Cor MM",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "dbcd", colorPath: "strokeDbcd.color" },
          { key: "mm", colorPath: "strokeMm.color" },
        ],
      },
      tooltip: tooltipTypes.DBCDTooltip,
      // Razão sem limites, simétrica em torno de zero — auto-fit puro.
      yExtentKeys: ["dbcd"],
      yExtents: (plotData) => {
        const values = plotData
          .flatMap((d) => [d.dbcd?.dbcd, d.dbcd?.mm])
          .filter((v) => v !== undefined && v !== null && !isNaN(v));
        if (values.length === 0) return [-0.01, 0.01];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = Math.max((max - min) * 0.1, 1e-9);
        return [min - padding, max + padding];
      },
      description:
        "Convergência e Divergência de BIAS: suaviza a diferença entre o BIAS atual e o BIAS de M barras atrás, com uma média de sinal. O cruzamento das linhas marca o ponto de tomada de decisão do movimento.",
    },
    // LWR — Estocástico Rápido Invertido Suavizado (migrado do Silverlight FML.LWR).
    lwr: {
      displayName: "Estocástico Rápido Invertido Suavizado (LWR)",
      key: "lwr",
      type: indicatorTypes.lwr,
      series: seriesTypes.LWRSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "LWR",
      renderKey: "lwr",
      decimalPlaces: 2,
      defaultOptions: [
        NumericPropertyType({
          name: "period",
          displayName: "Período (N)",
          min: 1,
          max: 1000,
          defaultValue: 9,
        }),
        NumericPropertyType({
          name: "m1",
          displayName: "Suavização 1 (M1)",
          min: 1,
          max: 1000,
          defaultValue: 3,
        }),
        NumericPropertyType({
          name: "m2",
          displayName: "Suavização 2 (M2)",
          min: 1,
          max: 1000,
          defaultValue: 3,
        }),
      ],
      defaultOptionsForAppearance: [
        // Cores default do engine (paleta por índice): LWR1=Yellow, LWR2=Red.
        StrokeType({
          name: "strokeLwr1",
          displayName: "Cor LWR1",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeLwr2",
          displayName: "Cor LWR2",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "lwr1", colorPath: "strokeLwr1.color" },
          { key: "lwr2", colorPath: "strokeLwr2.color" },
        ],
      },
      tooltip: tooltipTypes.LWRTooltip,
      // Estocástico invertido: limitado a [0,100] — auto-fit com clamp.
      yExtentKeys: ["lwr"],
      yExtents: (plotData) => {
        const values = plotData
          .flatMap((d) => [d.lwr?.lwr1, d.lwr?.lwr2])
          .filter((v) => v !== undefined && v !== null && !isNaN(v));
        if (values.length === 0) return [0, 100];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = Math.max((max - min) * 0.1, 1);
        return [Math.max(0, min - padding), Math.min(100, max + padding)];
      },
      description:
        "Estocástico Rápido Invertido Suavizado: o Estocástico Rápido invertido (100 − FastSTO) e suavizado por duas médias sucessivas. Escala 0–100.",
    },
    // FastSTO — Estocástico Rápido (migrado do Silverlight FML.FastSTO, #368).
    // Linhas 80/20 do legado (params UpperLine/LowerLine) viram aparência aqui.
    fastSto: {
      displayName: "Estocástico Rápido (FastSTO)",
      key: "fastSto",
      type: indicatorTypes.fastSto,
      series: seriesTypes.FastSTOSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "FASTSTO",
      renderKey: "fastSto",
      decimalPlaces: 2,
      defaultOptions: [
        NumericPropertyType({
          name: "period",
          displayName: "Período (N)",
          min: 1,
          max: 1000,
          defaultValue: 14,
        }),
        NumericPropertyType({
          name: "m",
          displayName: "Suavização %D (M)",
          min: 1,
          max: 1000,
          defaultValue: 3,
        }),
      ],
      defaultOptionsForAppearance: [
        // Cores default do engine (paleta por índice, como no LWR): K=Yellow, D=Red.
        StrokeType({
          name: "strokeK",
          displayName: "Cor %K",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeD",
          displayName: "Cor %D",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        BooleanType({
          name: "showZones",
          displayName: "Linhas 80/20",
          defaultValue: true,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        ThresholdLineType({
          name: "overBought",
          displayName: "Sobrecompra",
          defaultValue: {
            value: 80,
            stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 0,
          max: 100,
        }),
        ThresholdLineType({
          name: "overSold",
          displayName: "Sobrevenda",
          defaultValue: {
            value: 20,
            stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 0,
          max: 100,
        }),
      ],
      labelConfig: {
        lines: [
          { key: "k", colorPath: "strokeK.color" },
          { key: "d", colorPath: "strokeD.color" },
        ],
      },
      tooltip: tooltipTypes.FastSTOTooltip,
      // Estocástico: limitado a [0,100] — auto-fit com clamp, garantindo as
      // linhas 80/20 visíveis (lição VRSI: nunca [0,100] fixo).
      yExtentKeys: ["fastSto"],
      yExtents: (plotData) => {
        const values = plotData
          .flatMap((d) => [d.fastSto?.k, d.fastSto?.d])
          .filter((v) => v !== undefined && v !== null && !isNaN(v));
        if (values.length === 0) return [0, 100];
        const min = Math.min(...values, 20);
        const max = Math.max(...values, 80);
        const padding = Math.max((max - min) * 0.1, 1);
        return [Math.max(0, min - padding), Math.min(100, max + padding)];
      },
      description:
        "Estocástico Rápido (FastSTO): %K = posição do fechamento no range de N períodos, (Fech − Mín(N)) / (Máx(N) − Mín(N)) × 100; %D = média móvel simples de M períodos do %K. Escala 0–100, com referências 80/20.",
    },
    // B3612 — Cruzamento de Médias BIAS (migrado do Silverlight FML.B3612).
    // Sem parâmetros: as janelas 3/6/12 são fixas no engine original.
    b3612: {
      displayName: "Cruzamento de Médias BIAS (B3612)",
      key: "b3612",
      type: indicatorTypes.b3612,
      series: seriesTypes.B3612Series,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "B3612",
      renderKey: "b3612",
      decimalPlaces: 2,
      defaultOptions: [],
      defaultOptionsForAppearance: [
        // Cores default do engine (paleta por índice): B36=Yellow, B612=Red.
        StrokeType({
          name: "strokeB36",
          displayName: "Cor B36",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeB612",
          displayName: "Cor B612",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "b36", colorPath: "strokeB36.color" },
          { key: "b612", colorPath: "strokeB612.color" },
        ],
      },
      tooltip: tooltipTypes.B3612Tooltip,
      // Oscilador simétrico em torno de zero, sem limites — auto-fit puro.
      yExtentKeys: ["b3612"],
      yExtents: (plotData) => {
        const values = plotData
          .flatMap((d) => [d.b3612?.b36, d.b3612?.b612])
          .filter((v) => v !== undefined && v !== null && !isNaN(v));
        if (values.length === 0) return [-1, 1];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = Math.max((max - min) * 0.1, 1e-6);
        return [min - padding, max + padding];
      },
      description:
        "Cruzamento de Médias BIAS: diferença entre médias aritméticas do fechamento — MA(3)−MA(6) e MA(6)−MA(12). O cruzamento das duas linhas marca o ponto de tomada de decisão do movimento.",
    },
    // SRDM — Máximas e Mínimas (migrado do Silverlight FML.SRDM).
    srdm: {
      displayName: "Máximas e Mínimas (SRDM)",
      key: "srdm",
      type: indicatorTypes.srdm,
      series: seriesTypes.SRDMSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "SRDM",
      renderKey: "srdm",
      decimalPlaces: 4,
      defaultOptions: [
        NumericPropertyType({
          name: "period",
          displayName: "Período (N)",
          min: 1,
          max: 1000,
          defaultValue: 30,
        }),
      ],
      defaultOptionsForAppearance: [
        // Cores default do engine (paleta por índice): SRDM=Yellow, ASRDM=Red.
        StrokeType({
          name: "strokeSrdm",
          displayName: "Cor SRDM",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeAsrdm",
          displayName: "Cor ASRDM",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "srdm", colorPath: "strokeSrdm.color" },
          { key: "asrdm", colorPath: "strokeAsrdm.color" },
        ],
      },
      tooltip: tooltipTypes.SRDMTooltip,
      // Razão limitada a [0,1] por construção — auto-fit com clamp.
      yExtentKeys: ["srdm"],
      yExtents: (plotData) => {
        const values = plotData
          .flatMap((d) => [d.srdm?.srdm, d.srdm?.asrdm])
          .filter((v) => v !== undefined && v !== null && !isNaN(v));
        if (values.length === 0) return [0, 1];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = Math.max((max - min) * 0.1, 0.01);
        return [Math.max(0, min - padding), Math.min(1, max + padding)];
      },
      description:
        "Máximas e Mínimas: compara a força dos movimentos de alta e de baixa das máximas/mínimas em relação à barra anterior, com uma linha suavizada. O cruzamento das linhas marca o ponto de tomada de decisão do movimento.",
    },
    so: {
      displayName: "Oscilador Estocástico (SO)",
      key: "so",
      type: indicatorTypes.so,
      series: seriesTypes.SOSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "SO",
      renderKey: "so",
      decimalPlaces: 4,
      defaultOptions: [
        NumericPropertyType({
          name: "n1",
          displayName: "Período SK (N1)",
          min: 1,
          max: 1000,
          defaultValue: 3,
        }),
        NumericPropertyType({
          name: "n2",
          displayName: "Período SD (N2)",
          min: 1,
          max: 1000,
          defaultValue: 3,
        }),
      ],
      defaultOptionsForAppearance: [
        // Cores default do engine (paleta por índice): SK=Yellow, SD=Red.
        StrokeType({
          name: "strokeSk",
          displayName: "Cor SK",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeSd",
          displayName: "Cor SD",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "sk", colorPath: "strokeSk.color" },
          { key: "sd", colorPath: "strokeSd.color" },
        ],
      },
      tooltip: tooltipTypes.SOTooltip,
      description:
        "Oscilador Estocástico: posição do fechamento no range da própria barra, (Fech−Mín)/(Máx−Mín), duplamente suavizada (SK, SD). Escala 0–1. Não confundir com o Estocástico de N períodos (rolling).",
    },
    rsiCutler: {
      displayName: "IFR Cutler (RSICutler)",
      key: "rsiCutler",
      type: indicatorTypes.rsiCutler,
      series: seriesTypes.RSICutlerSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 9,
          min: 1,
          max: 1000,
        }),
        NumericPropertyType({
          name: "average",
          displayName: "Média do sinal (0 = oculta)",
          defaultValue: 5,
          min: 0,
          max: 1000,
          step: 1,
        }),
      ],
      defaultOptionsForAppearance: [
        // Cores = paleta default do engine Silverlight (fórmula não seta cor
        // explícita; observadas no OOB vivo): RSI=Yellow, EMARSI=Red(Width2),
        // Center=#0000B4 pontilhado, Upper=DarkGray, Lower=DodgerBlue tracejados.
        StrokeType({
          name: "strokeRsi",
          displayName: "IFR",
          defaultValue: {
            color: "#FFFF00",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeEmaRsi",
          displayName: "Sinal (EMA)",
          defaultValue: {
            color: "#FF0000",
            dash: "Solid",
            width: 2,
            opacity: 1,
          },
        }),
        ThresholdLineType({
          name: "centerLine",
          displayName: "Linha Central",
          defaultValue: {
            value: 50,
            stroke: { color: "#0000B4", dash: "Dot", width: 1, opacity: 1 },
          },
          min: 0,
          max: 100,
        }),
        ThresholdLineType({
          name: "overBought",
          displayName: "Sobrecompra",
          defaultValue: {
            value: 70,
            stroke: { color: "#A9A9A9", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 50,
          max: 100,
        }),
        ThresholdLineType({
          name: "overSold",
          displayName: "Sobrevenda",
          defaultValue: {
            value: 30,
            stroke: { color: "#1E90FF", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 0,
          max: 50,
        }),
      ],
      tooltip: tooltipTypes.RSICutlerTooltip,
      labelConfig: {
        lines: [
          { key: "rsi", colorPath: "strokeRsi.color" },
          { key: "emaRsi", colorPath: "strokeEmaRsi.color" },
        ],
      },
      renderKey: "rsiCutler",
      yAxisId: "RSICutler",
      separatePanel: true,
      yExtents: (plotData, indicator) => {
        const { RSICutlerSeries } = seriesTypes;
        return RSICutlerSeries.yExtents(
          plotData,
          undefined,
          plotData,
          indicator,
        );
      },
      decimalPlaces: 1,
      description:
        "IFR (Índice de Força Relativa) variante Cutler — suaviza ganhos/perdas com média móvel ARITMÉTICA (não Wilder): RSI = 100·P/(P+N), com linha de sinal EMA.",
    },
    stochRsi: {
      displayName: "IFR Estocástico (StochRSI)",
      key: "stochRsi",
      type: indicatorTypes.stochRsi,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 14,
          min: 1,
          max: 1000,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          defaultValue: {
            color: "#26C6DA",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "StochRSI",
      },
      renderKey: "stochRsi",
      // Auto-fit como o engine (o Run() não crava eixo). StochRSI é normalizado
      // em [0,1] por construção — clamp nos limites matemáticos.
      yExtentKeys: ["stochRsi"],
      yExtents: (plotData) => {
        const values = plotData
          .map((d) => d.stochRsi)
          .filter((v) => v !== undefined && v !== null && !isNaN(v));
        if (values.length === 0) return [0, 1];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = (max - min) * 0.1 || 0.05;
        return [Math.max(0, min - padding), Math.min(1, max + padding)];
      },
      yAxisId: "StochRsi",
      separatePanel: true,
      decimalPlaces: 3,
      description:
        "IFR Estocástico (Chande & Kroll): normaliza o RSI para a faixa 0–1 pela sua própria amplitude numa janela de N períodos, medindo onde o RSI está em relação à sua escala recente.",
    },
    momentum: {
      displayName: "Momentum Indicator",
      key: "momentum",
      type: indicatorTypes.momentum,
      series: seriesTypes.TRIXSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      defaultOptions: [
        WindowSizeType({
          name: "windowSize",
          displayName: "Período",
          defaultValue: 14,
          min: 1,
          max: 100,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Cor da Linha",
          defaultValue: {
            color: "#9C27B0",
            dash: "Solid",
            width: 2,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "zeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#B8C2CC",
            dash: "ShortDash",
            width: 1,
            opacity: 0.7,
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "Momentum",
      },
      renderKey: "momentum",
      yAxisId: "Momentum",
      separatePanel: true,
      yExtents: (plotData, indicator) => {
        const { TRIXSeries } = seriesTypes;
        return TRIXSeries.yExtents(plotData, undefined, plotData, indicator);
      },
      description:
        "Mede a taxa de mudança do preço: Close[i] - Close[i - Period]. Valores positivos indicam momentum ascendente, negativos descendente. Linha zero indica momentum neutro.",
    },
    change: {
      displayName: "Change (Variação %)",
      key: "change",
      type: indicatorTypes.change,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo de Preço",
          defaultValue: "close",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha de Variação",
          defaultValue: {
            color: "#2196f3",
            dash: "Solid",
            width: 2,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "Variação %",
        yDisplayFormat: (d) => `${(d || 0).toFixed(2)}%`,
      },
      yAxisId: "Change",
      separatePanel: true,
      renderKey: "change", // This should match the accessor in the indicator
      decimalPlaces: 2,
      description:
        "Mostra a variação percentual entre períodos consecutivos. Útil para análise de momentum e volatilidade de curto prazo.",
    },
    elderRay: {
      displayName: "Elder Ray (Bull/Bear Power)",
      key: "elderRay",
      type: indicatorTypes.elderRay,
      series: seriesTypes.ElderRaySeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      defaultOptions: [
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo de Preço",
          defaultValue: "close",
        }),
      ],
      defaultOptionsForAppearance: [
        ColorType({
          name: "bullPowerFill",
          displayName: "Cor Bull Power",
          defaultValue: { color: "#4caf50" },
        }),
        ColorType({
          name: "bearPowerFill",
          displayName: "Cor Bear Power",
          defaultValue: { color: "#f44336" },
        }),
      ],
      transformAppearanceConfig: (config) => {
        // Extract color strings from ColorType objects for ElderRaySeries
        return {
          ...config,
          bullPowerFill: config.bullPowerFill?.color || "#4caf50",
          bearPowerFill: config.bearPowerFill?.color || "#f44336",
        };
      },
      tooltip: tooltipTypes.ElderRayTooltip,
      yAxisId: "ElderRay",
      separatePanel: true,
      renderKey: "elderRay",
      description:
        "Mede o poder dos touros (Bull Power) e ursos (Bear Power) comparando máximas/mínimas com uma média móvel. Valores positivos indicam força compradora, negativos força vendedora.",
    },
    trix: {
      displayName: "TRIX",
      key: "trix",
      type: indicatorTypes.trix,
      series: seriesTypes.TRIXSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 14,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Cor da Linha",
          defaultValue: {
            color: "#808080",
            dash: "Solid",
            width: 2,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "zeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#999999",
            dash: "ShortDash",
            width: 1,
            opacity: 0.8,
          },
        }),
      ],
      tooltip: tooltipTypes.TRIXTooltip,
      yAxisId: "TRIX",
      separatePanel: true,
      renderKey: "trix",
      yExtents: (plotData, indicator) => {
        const { TRIXSeries } = seriesTypes;
        return TRIXSeries.yExtents(plotData, undefined, plotData, indicator);
      },
      decimalPlaces: 4,
      description:
        "Oscilador baseado em triple EMA que mede a variação percentual suavizada, ajudando a identificar mudanças de momentum.",
    },
    priceRateOfChange: {
      displayName: "Taxa de variação de preço",
      key: "priceRateOfChange",
      type: indicatorTypes.priceRateOfChange,
      series: seriesTypes.TRIXSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 14,
          min: 1,
          max: 500,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          defaultValue: {
            color: "#9C27B0",
            dash: "Solid",
            width: 2,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "zeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#B8C2CC",
            dash: "ShortDash",
            width: 1,
            opacity: 0.7,
          },
        }),
      ],
      tooltip: tooltipTypes.PriceROCTooltip,
      renderKey: "priceRateOfChange",
      yAxisId: "PriceROC",
      separatePanel: true,
      yExtents: (plotData, indicator) => {
        const { TRIXSeries } = seriesTypes;
        return TRIXSeries.yExtents(plotData, undefined, plotData, indicator);
      },
      description:
        "Taxa de variação percentual do preço. Calcula: 100 × ((Preço atual / Preço N períodos atrás) - 1). Valores positivos indicam alta, negativos indicam queda. Linha zero indica preço igual ao período de referência.",
    },
    linearRegressionR2: {
      displayName: "Lin R2 (Linear Reg R2)",
      key: "linearRegressionR2",
      type: indicatorTypes.linearRegressionR2,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 14,
          min: 2,
          max: 500,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha R²",
          defaultValue: {
            color: "#2196F3",
            dash: "Solid",
            width: 2,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.LinearRegressionR2Tooltip,
      renderKey: "linearRegressionR2",
      yAxisId: "LinR2",
      separatePanel: true,
      yExtents: (data) => {
        const values = data
          .map((d) => d.linearRegressionR2)
          .filter((d) => d != null && !isNaN(d));

        if (values.length === 0) return [0, 1];

        const min = Math.min(...values, 0);
        const max = Math.max(...values, 1);
        const padding = (max - min) * 0.1;

        return [Math.max(0, min - padding), Math.min(1, max + padding)];
      },
      decimalPlaces: 3,
      description:
        "Coeficiente de determinação R² da regressão linear. Mede o quão bem o preço se ajusta a uma linha de tendência linear. Valores próximos de 1 indicam forte tendência linear, valores próximos de 0 indicam movimento sem direção definida. Range: 0-1.",
    },
    coppock: {
      displayName: "Curva de Coppock (Coppock Curve)",
      key: "coppock",
      type: indicatorTypes.coppock,
      series: seriesTypes.TRIXSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período WMA",
          defaultValue: 10,
          min: 1,
          max: 100,
        }),
        WindowSizeType({
          name: "shortRoC",
          displayName: "RoC Curto",
          defaultValue: 11,
          min: 1,
          max: 100,
        }),
        WindowSizeType({
          name: "longRoC",
          displayName: "RoC Longo",
          defaultValue: 14,
          min: 1,
          max: 100,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha Coppock",
          defaultValue: {
            color: "#9C27B0",
            dash: "Solid",
            width: 2,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "zeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#B8C2CC",
            dash: "ShortDash",
            width: 1,
            opacity: 0.7,
          },
        }),
      ],
      tooltip: tooltipTypes.CoppockTooltip,
      renderKey: "coppock",
      yAxisId: "Coppock",
      separatePanel: true,
      yExtents: (plotData, indicator) => {
        const { TRIXSeries } = seriesTypes;
        return TRIXSeries.yExtents(plotData, undefined, plotData, indicator);
      },
      decimalPlaces: 2,
      description:
        "Oscilador de momentum de longo prazo desenvolvido por Edwin Coppock. Calcula a média móvel ponderada da soma de duas taxas de variação (RoC). Tradicionalmente usado para identificar pontos de virada em mercados de baixa. Crossover da linha zero sinaliza mudanças de tendência.",
    },
  },
};

export default momentumRegistry;
